import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { RootState } from '../index'

export interface FeedbackTicket {
  id: string
  subject: string
  description: string
  type: 'bug' | 'feedback'
  severity?: string
  category?: string
  status: string
  referenceNumber: string
  resolution?: string
  screenshotUrl?: string
  createdAt: string
  updatedAt: string
  user?: {
    id: string
    email: string
    username: string
    firstName?: string
    lastName?: string
  }
}

export interface SubmitFeedbackRequest {
  type: 'bug' | 'feedback'
  subject: string
  description: string
  severity?: string
  category?: string
  screenshotUrl?: string
}

export interface SubmitFeedbackResponse {
  id: string
  referenceNumber: string
  status: string
  createdAt: string
}

export interface ApiResponse<T> {
  success: boolean
  message?: string
  data: T
  pagination?: { total: number; page: number; limit: number; pages: number }
}

export const feedbackApi = createApi({
  reducerPath: 'feedbackApi',
  baseQuery: fetchBaseQuery({
    baseUrl: '/api/v1',
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token
      if (token) {
        headers.set('authorization', `Bearer ${token}`)
      }
      return headers
    },
  }),
  tagTypes: ['Feedback'],
  endpoints: (builder) => ({
    submitFeedback: builder.mutation<SubmitFeedbackResponse, SubmitFeedbackRequest>({
      query: (body) => ({ url: '/feedback', method: 'POST', body }),
      transformResponse: (response: ApiResponse<SubmitFeedbackResponse>) => response.data,
      invalidatesTags: ['Feedback'],
    }),

    getUserFeedback: builder.query<FeedbackTicket[], void>({
      query: () => '/feedback',
      transformResponse: (response: ApiResponse<FeedbackTicket[]>) => response.data,
      providesTags: ['Feedback'],
    }),

    adminListFeedback: builder.query<ApiResponse<FeedbackTicket[]>, { type?: string; status?: string; page?: number }>({
      query: ({ type, status, page = 1 } = {}) => {
        const params = new URLSearchParams()
        if (type) params.set('type', type)
        if (status) params.set('status', status)
        params.set('page', page.toString())
        return `/admin/support/tickets?${params.toString()}`
      },
      providesTags: ['Feedback'],
    }),

    adminUpdateFeedback: builder.mutation<FeedbackTicket, { id: string; status?: string; resolution?: string; assignedTo?: string }>({
      query: ({ id, ...body }) => ({ url: `/admin/support/tickets/${id}`, method: 'PUT', body }),
      transformResponse: (response: ApiResponse<FeedbackTicket>) => response.data,
      invalidatesTags: ['Feedback'],
    }),
  }),
})

export const {
  useSubmitFeedbackMutation,
  useGetUserFeedbackQuery,
  useAdminListFeedbackQuery,
  useAdminUpdateFeedbackMutation,
} = feedbackApi
